/**
 * Combine all reducers in this file and export the combined reducers.
 */
import { combineReducers } from 'redux';
import { connectRouter } from 'connected-react-router';
import {
  type Action,
  createSlice,
  type PayloadAction,
  type Reducer,
  type ReducersMapObject,
  type StateFromReducersMapObject,
} from '@reduxjs/toolkit';
import { append, assoc, assocPath, findIndex, propEq, mapObjIndexed } from 'ramda';
import { castDraft, type Draft } from 'immer';
import history from '@/utils/history';
import { authActions } from '@/containers/App/actions';
import authReducer from './containers/App/authReducer';
import appReducer from './containers/AuthenticatedApp/reducer';

export const load = assoc('isLoading', true);
export const success = <T>(
  _: unknown,
  { payload }: { payload: T },
): {
  isLoading: boolean;
  data: T;
} => ({
  isLoading: false,
  data: payload,
});
export const error = assoc('isLoading', false);

export interface ListState<T> {
  isLoading: boolean;
  data: {
    result?: T[];
    count?: number;
  };
}

export const createSuccess = <T>(state: Draft<ListState<T>>, { payload }: { payload: T }): Draft<ListState<T>> => {
  const { data } = state;

  const count = data.count ?? 0;
  const result = data.result ?? [];

  return success(state, {
    payload: {
      result: append(castDraft(payload), result), // official solution to make TypeScript happy
      count: count + 1,
    },
  });
};

export interface Identifiable {
  _id: string;
}

export const removeSuccess = <T extends Identifiable>(
  state: Draft<ListState<T>>,
  { payload }: { payload: T },
): Draft<ListState<T>> => {
  const { data } = state;

  const count = data.count ?? 0;
  const result = data.result ?? [];

  return success(state, {
    payload: {
      result: result.filter(t => t._id !== payload._id),
      count: count - 1,
    },
  });
};

export const setInitial =
  <S>(initialState: S) =>
  (): S =>
    initialState;
export const setNull = (): null => null;

export interface ReadonlyListState<T> {
  isLoading: boolean;
  data: T;
}

export const createReadonlyListSlice = <T, LoadPayload = void, ErrorPayload = Error | undefined>({
  name,
  initialState,
}: {
  name: string;
  initialState: ReadonlyListState<T>;
}) =>
  createSlice({
    name,
    initialState,
    reducers: {
      load: (state, action: PayloadAction<LoadPayload>) => load(state),
      loadSuccess: (state, action: PayloadAction<T>) => success(state, action),
      loadError: (state, action: PayloadAction<ErrorPayload>) => error(state),
    },
    extraReducers: {
      [authActions.logoutFromCustomer.success.type]: setInitial(initialState),
      [authActions.logout.success.type]: setInitial(initialState),
    },
  });

export const createListSlice = <
  Entity extends Identifiable,
  LoadPayload = void,
  CreatePayload = Entity,
  EditPayload = Entity,
  RemovePayload = Entity,
  ErrorPayload = unknown,
>({
  name,
  initialState,
}: {
  name: string;
  initialState: ListState<Entity>;
}) =>
  createSlice({
    name,
    initialState,
    reducers: {
      load: (state, action: PayloadAction<LoadPayload>) => load(state),
      loadSuccess: (state, action: PayloadAction<ListState<Entity>['data']>) => success(state, action),
      loadError: (state, action: PayloadAction<ErrorPayload>) => error(state),

      create: (state, action: PayloadAction<CreatePayload>) => load(state),
      createSuccess: (state, action: PayloadAction<Entity>) => createSuccess(state, action),
      createError: (state, action: PayloadAction<ErrorPayload>) => error(state),

      edit: (state, action: PayloadAction<EditPayload>) => load(state),
      editSuccess: (state, { payload }: PayloadAction<Entity>) => ({
        data: assocPath(
          ['result', findIndex(propEq('_id', payload._id), state.data.result ?? [])],
          payload,
          state.data,
        ),
        isLoading: false,
      }),
      editError: (state, action: PayloadAction<ErrorPayload>) => error(state),

      remove: (state, action: PayloadAction<RemovePayload>) => load(state),
      removeSuccess: (state, action: PayloadAction<Entity>) => removeSuccess(state, action),
      removeError: (state, action: PayloadAction<ErrorPayload>) => error(state),
    },
    extraReducers: {
      [authActions.logoutFromCustomer.success.type]: setInitial(initialState),
      [authActions.logout.success.type]: setInitial(initialState),
    },
  });

const resetActions: string[] = [authActions.logoutFromCustomer.success.type, authActions.logout.success.type];

const resetReducerOnAction =
  <S, A extends Action>(reducer: Reducer<S, A>): Reducer<S, A> =>
  (state, action) => {
    if (resetActions.includes(action.type as string)) {
      return reducer(undefined, action);
    }
    return reducer(state, action);
  };

const makeReducersLogoutable = (object: ReducersMapObject) => mapObjIndexed(resetReducerOnAction, object);

const staticReducers = {
  router: connectRouter(history),
  auth: authReducer,
  app: resetReducerOnAction(appReducer),
};

export type StaticRootState = StateFromReducersMapObject<typeof staticReducers>;

/**
 * Creates the main reducer with the dynamically injected ones
 */
export default function createReducer(injectedReducers: ReducersMapObject) {
  return combineReducers({
    ...staticReducers,
    ...makeReducersLogoutable(injectedReducers),
  });
}
